// hooks/useOrder.js
import {
    RUNNING_ORDERS_PAGE_SIZE,
    isRunningOnly,
} from "components/Order/RunningOrdersToggle/RunningOrdersToggle";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    createDispatchOrder,
    getAllOrders,
    getOrder,
    getOrderStats,
    removeOrder,
    removeOrders,
    softDeleteOrder as softDeleteOrderRequest,
    updateOrder,
} from "services/order";
import { setOrders, setOrderStats, updateOrderInList } from "store/orderSlice";
import { useSearchParams } from "react-router-dom";
import { readLoadError } from "utils/apiError";

/**
 * Order data hook for the VENDOR-audience screen (/orders) and both detail
 * pages. Near-identical twin of hooks/useDeliveryOrder — see that file for the
 * full list of differences; in short this one owns the `order` Redux slice,
 * defaults the soft-delete filter to `deletedForVendor=false`, and is the only
 * one of the two that can CREATE orders (order entry lives only on /orders).
 *
 * Why two hooks instead of one parameterised hook: /orders and /deliveryOrders
 * are routinely open in two tabs and must keep independent page, filter and
 * aggregate state. Sharing one slice would make each screen clobber the
 * other's pagination and stat boxes.
 *
 * PARAM DUPLICATION (deliberate, but a trap): this hook derives its own
 * `params` from the URL search params, and components/Order/Order.js derives
 * the same params again via its own `buildOrderParams`. The page's copy drives
 * the visible list; the hook's copy is used ONLY for the refetch that follows
 * a mutation. The two are built from the same source so they agree, but the
 * hook's copy is set in an effect and can therefore lag one render behind
 * during a filter change.
 *
 * ERROR CONVENTION: `error` is set on every failure but nothing renders it —
 * user-facing errors come from each caller's own toast. `createQuickOrder`
 * RETHROWS so its callers can show the API's Arabic message; the remaining
 * mutations return the error object instead, so a caller that treats them alike
 * will silently swallow a failure.
 */
const useOrder = () => {
    const orders = useSelector((state) => state.order.orders);
    const page = useSelector((state) => state.order.page);
    const adminInfo = useSelector((state) => state.auth.userData);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Why the LIST did not load, or null. `error` above is shared by every
    // action of this hook and is never cleared, so the page cannot use it to
    // tell a failed load from an empty list.
    const [listError, setListError] = useState(null);
    const dispatch = useDispatch();

    const [params, setParams] = useState({});
    const [searchParams] = useSearchParams();

    // Mirrors the URL search params into a request-params object. The URL is
    // the source of truth for filters so an operator can bookmark or share a
    // filtered view; `page` alone lives in Redux.
    //
    // City scoping is decided here, not by the caller: a super_admin may pick
    // any city (or none, meaning all cities), while a city-scoped admin is
    // pinned to their own `cityId` and cannot widen it from the URL.
    useEffect(() => {
        const newParams = {};
        // «عرض الطلبات الجارية فقط» shows every running order on one page.
        const runningOnly = isRunningOnly(searchParams);
        newParams.page = runningOnly ? 1 : page;
        newParams.size = runningOnly ? RUNNING_ORDERS_PAGE_SIZE : 12;

        if (searchParams.get("vendor")) {
            newParams.vendorId = searchParams.get("vendor");
        }
        if (searchParams.get("delivery")) {
            newParams.deliveryId = searchParams.get("delivery");
        }
        if (searchParams.get("status")) {
            newParams.status = searchParams.get("status");
        }
        if (searchParams.get("startDate")) {
            newParams.startDate = searchParams.get("startDate");
        }
        if (searchParams.get("endDate")) {
            newParams.endDate = searchParams.get("endDate");
        }
        if (searchParams.get("search")) {
            newParams.search = searchParams.get("search");
        }
        // «عرض الطلبات الجارية فقط». The API owns the list of statuses.
        if (runningOnly) {
            newParams.inProgress = "true";
        }
        const selectedCity = searchParams.get("city");
        const resolvedCityId = adminInfo?.super_admin ? selectedCity : adminInfo?.cityId;

        if (resolvedCityId) {
            newParams.cityId = resolvedCityId;
        }
        // "الطلبات المحذوفة" filter — sourced from the `deleted_for_vendor`
        // flag. Default: hide deleted. "all" → show both. "true" → only
        // deleted.
        const deletedFilter = searchParams.get("deletedForVendor");
        if (deletedFilter === "all") {
            // Show deleted too: don't filter
        } else if (deletedFilter === "true") {
            newParams.deletedForVendor = "true";
        } else {
            newParams.deletedForVendor = "false";
        }

        setParams(newParams);
    }, [adminInfo?.cityId, adminInfo?.super_admin, page, searchParams]);

    /**
     * Refreshes the aggregate cards independently from the order rows. The
     * caller decides when filters—not pagination—have changed.
     *
     * @param {object} requestParams - Current order filters.
     * @returns {Promise<object|undefined>}
     */
    const fetchOrderStats = useCallback(
        async (requestParams) => {
            try {
                const data = await getOrderStats(requestParams, true);
                dispatch(setOrderStats(data));
                return data;
            } catch (err) {
                setError(err.message || "Failed to fetch order stats");
            }
        },
        [dispatch],
    );

    // Fetch Order
    const fetchOrders = useCallback(
        async (params) => {
            setLoading(true);
            try {
                const data = await getAllOrders(params);

                dispatch(setOrders(data));
                setListError(null);
                return data;
            } catch (err) {
                setError(err.message || "Failed to fetch orders");
                // The rows in the store belong to the filters of the last
                // good load. The page hides them and shows this reason.
                setListError(readLoadError(err));
            } finally {
                setLoading(false);
            }
        },
        [dispatch],
    );

    const refetchOrders = useCallback(
        async (params) => {
            fetchOrderStats(params);
            try {
                const data = await getAllOrders(params);

                dispatch(setOrders(data));
                setListError(null);
                return data;
            } catch (err) {
                // A refresh in the background. The rows on screen stay: one
                // lost request must not empty the page.
                setError(err.message || "Failed to fetch orders");
            } finally {
                // no cleanup needed
            }
        },
        [dispatch, fetchOrderStats],
    );
    // Fetch Order
    const fetchOrderBYId = async (id) => {
        setLoading(true);
        try {
            const data = await getOrder(id);

            return data.order;
        } catch (err) {
            setError(err.message || "Failed to fetch products");
        } finally {
            setLoading(false);
        }
    };

    /**
     * Patches one order and keeps the table responsive by updating Redux before
     * the request resolves — the status dropdown, prep-time stepper and driver
     * select all sit inside table rows where a spinner would be disruptive.
     *
     * Rollback is a full refetch rather than a snapshot restore, so a failed
     * edit briefly shows the optimistic value before snapping back.
     *
     * CONSTRAINT: failures are REPORTED BY RETURN VALUE, not by throwing —
     * callers that ignore the return (driver assign, driver reset) fail
     * silently. Only the status dropdown inspects it.
     *
     * @param {number|string} id - Order id.
     * @param {FormData|object} updatedData - Partial order fields. FormData is
     *   flattened for the optimistic Redux patch only; the request keeps the
     *   original FormData. Pass a plain object when a field must be cleared —
     *   FormData stringifies values, so `deliveryId: null` would arrive as the
     *   string "null".
     */
    const editOrder = async (id, updatedData) => {
        // Extract plain object from FormData or use as-is
        const data = {};
        if (updatedData instanceof FormData) {
            updatedData.forEach((value, key) => {
                data[key] = value;
            });
        } else {
            Object.assign(data, updatedData);
        }

        // Optimistically update the order in Redux immediately
        dispatch(updateOrderInList({ id, data }));

        try {
            const updated = await updateOrder(id, updatedData);

            // Silent background refetch to sync with server (no loading spinner)
            refetchOrders({ ...params });

            return updated;
        } catch (err) {
            // Rollback: refetch the real data from server
            refetchOrders({ ...params });
            setError(err.response?.data?.message || "Failed to edit order");
            return err;
        }
    };

    /**
     * "طلب سريع" submit — shared by all three order modals.
     *
     * Replaces the former createDeliveryOrder / AddWhatsOrder pair, which posted
     * to two different URLs. The API now derives the order policy from the
     * logged-in user, so every modal makes the identical call and there is
     * nothing for the client to classify. Send only what the open modal
     * collects.
     *
     * RETHROWS on failure, adopting the better of the two behaviours it
     * replaces. The old createDeliveryOrder *returned* the axios error, so the
     * caller's `response.order` check fell through and the toast showed
     * "Request failed with status code 400" instead of the API's Arabic message
     * — which matters here, because the server is the only validator of the
     * money fields. Callers must catch and read `err.response?.data?.message`.
     *
     * The optional chaining is also load-bearing: a network-level failure has no
     * `response` at all, and the old un-guarded `err.response.data` threw inside
     * its own catch block.
     *
     * @param {FormData} data - The open modal's payload.
     */
    const createQuickOrder = async (data) => {
        setLoading(true);
        try {
            const created = await createDispatchOrder(data);

            refetchOrders({
                ...params,
            });

            return created;
        } catch (err) {
            setError(err.response?.data?.message || "تعذر إنشاء الطلب");
            throw err;
        } finally {
            setLoading(false);
        }
    };

    /**
     * HARD-deletes one order row (DELETE api/order/:id). Distinct from
     * `softDeleteOrder`, which only hides it from one audience — the row-menu
     * labels ("حذف" vs "حذف للمطعم فقط") are the only thing separating them in
     * the UI, and this one is irreversible.
     *
     * @param {number|string} id - Order id.
     */
    const deleteOrder = async (id) => {
        setLoading(true);
        try {
            await removeOrder(id);

            refetchOrders({
                ...params,
            });

            return true;
        } catch (err) {
            setError(err.message || "Failed to delete Order");
            return err;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Bulk SOFT delete for one audience — the "حذف طلبات المطعم" button, i.e.
     * the last step of the billing cycle: once a vendor has been settled, their
     * settled orders are hidden from the vendor view so the next cycle starts
     * clean.
     *
     * CONSTRAINT: the scope is whatever `deleteParams` says. Every narrowing
     * filter is optional server-side, so callers MUST spread the screen's live
     * list params — the page enforces this by only rendering the button while a
     * vendor filter is active. Passing only `deleteFor` would match every order.
     *
     * @param {object} deleteParams - The current list filters plus
     *   `deleteFor: "vendor"|"delivery"`.
     */
    const deleteOrders = async (deleteParams) => {
        setLoading(true);
        try {
            const data = await removeOrders({
                ...deleteParams,
            });

            refetchOrders({
                ...params,
            });

            // api#167: the sweep skips running orders and says how many.
            return { ok: true, updated: data?.updated, skippedRunning: data?.skippedRunning ?? 0 };
        } catch (err) {
            setError(err.message || "Failed to delete Order");
            return { ok: false, message: err.response?.data?.message };
        } finally {
            setLoading(false);
        }
    };

    /**
     * Soft-deletes one order for either vendor or delivery visibility and refreshes current listing filters.
     *
     * @param {number|string} id - Order id.
     * @param {"vendor"|"delivery"} deleteFor - Target audience visibility to remove.
     */
    const softDeleteOrder = async (id, deleteFor) => {
        setLoading(true);
        try {
            await softDeleteOrderRequest(id, deleteFor);

            refetchOrders({
                ...params,
            });

            return { ok: true };
        } catch (err) {
            setError(err.response?.data?.message || "Failed to soft delete order");
            // api#167: a running order answers 409 with the reason to show.
            return { ok: false, message: err.response?.data?.message };
        } finally {
            setLoading(false);
        }
    };
    return {
        orders,
        loading,
        error,
        listError,
        fetchOrders,
        fetchOrderStats,
        fetchOrderBYId,
        deleteOrder,
        editOrder,
        deleteOrders,
        softDeleteOrder,
        refetchOrders,
        createQuickOrder,
    };
};

export default useOrder;
